import { useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { useMutation } from '@apollo/client/react';

import {
  OrganizationForm,
  OrganizationRegistrationNumberTooltip,
  type IOrganizationForm,
} from '@/components/Organization';
import { BeneficiaryCreateDocument } from 'src/graphql/actions/beneficiary/beneficiaryCreate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';

import { useBeneficiaryContext } from '../model';

const CreateBeneficiaryDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { escrowId, canCreateBeneficiary } = useBeneficiaryContext();

  const [createBeneficiary] = useMutation(BeneficiaryCreateDocument);

  if (!canCreateBeneficiary) return null;

  const onCreateOrganization = async ({
    companyName,
    street,
    streetNumber,
    city,
    postalCode,
    region,
    country,
    companyWebsite,
    companyRegistration,
  }: IOrganizationForm) => {
    try {
      const { data } = await createBeneficiary({
        variables: {
          escrowId,
          beneficiaryParams: {
            companyName,
            street,
            streetNumber,
            city,
            postalCode,
            region,
            country,
            companyWebsite,
            companyRegistrationNumber: companyRegistration,
          },
        },
      });

      if (data?.beneficiaryCreate?.errors?.length) {
        throw new ClientError(data?.beneficiaryCreate?.errors[0]);
      } else {
        toast.success('Beneficiary Organization Details added!');
      }
    } catch (e: unknown) {
      getApiError(e, toast.error);
    }
  };

  const closeDialog = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus />
          Add details
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[976px] gap-0">
        <DialogHeader className="mb-6">
          <DialogTitle className="text-base font-bold">Enter organization details</DialogTitle>
          <DialogDescription className="text-xs">
            Enter the details of the organization. This includes the registered name and address of the entity, which
            will be listed in the escrow agreement.
          </DialogDescription>
        </DialogHeader>
        <OrganizationForm
          onSubmit={onCreateOrganization}
          onCancel={closeDialog}
          defaultValues={{
            companyName: '',
            city: '',
            postalCode: '',
            country: '',
            region: '',
            street: '',
            streetNumber: '',
            companyWebsite: '',
            companyRegistration: '',
          }}
          registrationTooltip={<OrganizationRegistrationNumberTooltip />}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CreateBeneficiaryDialog;
